import {
  DisplayOrderForVertical,
  VerticalOption,
} from '@amzn/aet-galactus-kit';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import Text from '@amzn/meridian/text';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  parseStringInOrder,
  extractMetricValue,
} from '../../../Dashboard/DashboardComponents/LighthouseMetrics/helper';
import {
  GeneralMetricType,
  LighthouseMetrics,
  LighthouseMetricOption,
} from '../../../Dashboard/DashboardComponents/LighthouseMetrics/types';
import { DisplayBenchmark } from './DisplayBenchmark';
import { Feature, isFeatureEnabled } from '../../../../config/FeaturesConfig';
import Tooltip from '@amzn/meridian/tooltip';
import {
  CONSUMER_VERTICALS,
  D2_VERTICALS,
  convertToVerticalOption,
} from '../../../../Util/Vertical';
import { getMetricStringIdAndDefaultMessage } from './helper';

interface SummaryProps {
  keys: Array<string>;
  vertical: string;
  lighthouseMetrics: LighthouseMetrics;
  achtEnabled: boolean;
  concessionEnabled: boolean;
  manageCoachingNotes: boolean;
  teamMetricTypes: Set<string>;
}

const Summary: React.FC<SummaryProps> = ({
  vertical,
  lighthouseMetrics,
  achtEnabled,
  concessionEnabled,
  manageCoachingNotes,
  teamMetricTypes,
}: SummaryProps): React.ReactElement => {
  const newCoachingEnabled = isFeatureEnabled(Feature.NEW_COACHING);

  const verticalOption = convertToVerticalOption(vertical);
  const widthChange =
    CONSUMER_VERTICALS.has(verticalOption) || D2_VERTICALS.has(verticalOption)
      ? '8.5%'
      : '10.2%';

  const displayLHTabelCell = (
    metricOption: string,
    index: number,
    nullArray: boolean[],
    firstOccurance: number,
    metrics?: LighthouseMetrics
  ) => {
    return (
      <TableCell key={metricOption + metrics?.userID}>
        {metrics && metrics[metricOption as keyof LighthouseMetrics] ? (
          extractMetricValue(metricOption, metrics) === null ? (
            <>
              {nullArray[index] && index !== firstOccurance ? (
                <>
                  <Text>--</Text>
                  {DisplayBenchmark({
                    metricOption: metricOption,
                    metrics: metrics,
                  })}
                </>
              ) : (
                <span style={{ paddingTop: '10px' }}>
                  <Text>
                    <FormattedMessage
                      id='cs_perfportal_no_data'
                      defaultMessage='No data'
                    />
                  </Text>
                  {DisplayBenchmark({
                    metricOption: metricOption,
                    metrics: metrics,
                  })}
                </span>
              )}
            </>
          ) : (metricOption as keyof LighthouseMetrics) === 'ACHT' ? (
            <>
              {extractMetricValue(metricOption, metrics)}{' '}
              <FormattedMessage id='cs_perfportal_min' defaultMessage='min' />
              {DisplayBenchmark({
                metricOption: metricOption,
                metrics: metrics,
              })}
            </>
          ) : (
            <>
              {parseStringInOrder(metricOption, metrics)}
              {DisplayBenchmark({
                metricOption: metricOption,
                metrics: metrics,
              })}
            </>
          )
        ) : (
          <span style={{ paddingTop: '10px' }}>
            <Text>
              <FormattedMessage
                id='cs_perfportal_no_data'
                defaultMessage='No data'
              />
            </Text>
          </span>
        )}
      </TableCell>
    );
  };

  const displayLHInOrder = (
    vertical: string,
    OrderMap: Map<string, string[]>,
    metric: LighthouseMetrics,
    teamMetricTypes: Set<string>
  ): JSX.Element[] => {
    const map = vertical
      ? OrderMap.get(vertical)!.filter(
          metric =>
            !(!achtEnabled && metric === LighthouseMetricOption.ACHT) &&
            !(
              CONSUMER_VERTICALS.has(convertToVerticalOption(vertical)) &&
              !concessionEnabled &&
              metric === LighthouseMetricOption.CONCESSION_RATE
            ) &&
            teamMetricTypes.has(metric)
        )
      : // TODO: Change this to VerticalOption.Total_V2 when we remove enableRAPRemovalAndCCXUpdate feature flag.
        OrderMap.get(VerticalOption.Total)!.filter(
          metric => !(!achtEnabled && metric === LighthouseMetricOption.ACHT)
        );

    let nullArray: boolean[] = [];

    map.forEach((metricIndex, index) => {
      if (metric !== undefined) {
        // This check is to aviod race condition that useEffect is triggered at the same time
        if (
          !(metric[metricIndex as keyof LighthouseMetrics] as GeneralMetricType)
        ) {
          return;
        }
        if (extractMetricValue(metricIndex, metric) !== null) {
          nullArray.push(false);
        } else nullArray.push(true);
      }
    });

    const firstOccurance = nullArray!.findIndex(x => x === true);

    if (vertical) {
      return OrderMap.get(vertical)!
        .filter(
          metric =>
            !(!achtEnabled && metric === LighthouseMetricOption.ACHT) &&
            !(
              CONSUMER_VERTICALS.has(convertToVerticalOption(vertical)) &&
              !concessionEnabled &&
              metric === LighthouseMetricOption.CONCESSION_RATE
            ) &&
            teamMetricTypes.has(metric)
        )
        .map((metricOption, index) =>
          displayLHTabelCell(
            metricOption,
            index,
            nullArray,
            firstOccurance,
            metric
          )
        );
    }
    // TODO: Change this to VerticalOption.Total_V2 when we remove enableRAPRemovalAndCCXUpdate feature flag.
    return OrderMap.get(VerticalOption.Total)!
      .filter(
        metric => !(!achtEnabled && metric === LighthouseMetricOption.ACHT)
      )
      .map((metricOption, index) =>
        displayLHTabelCell(
          metricOption,
          index,
          nullArray,
          firstOccurance,
          metric
        )
      );
  };

  const intl = useIntl();
  return (
    <Table headerRows={1} showDividers>
      <TableRow highlightOnHover={false}>
        <TableCell width='19%' alignmentVertical='bottom'>
          <div data-cy='performance-summary'>
            <FormattedMessage
              id='cs_perfportal_performanceSummary'
              defaultMessage='Performance Summary'
            />
          </div>
        </TableCell>

        {
          // Display the metrics in nature order
          vertical &&
            DisplayOrderForVertical.get(vertical)!
              .filter(
                metric =>
                  !(!achtEnabled && metric === LighthouseMetricOption.ACHT) &&
                  !(
                    CONSUMER_VERTICALS.has(verticalOption) &&
                    !concessionEnabled &&
                    metric === LighthouseMetricOption.CONCESSION_RATE
                  ) &&
                  teamMetricTypes.has(metric)
              )
              .map(metric => (
                <TableCell
                  width={widthChange}
                  key={metric + 'HEADER'}
                  alignmentVertical='bottom'
                >
                  <FormattedMessage
                    {...getMetricStringIdAndDefaultMessage(metric)}
                  />
                </TableCell>
              ))
        }

        {
          // Display the metrics in nature order
          !vertical &&
            // TODO: Change this to VerticalOption.Total_V2 when we remove enableRAPRemovalAndCCXUpdate feature flag.
            DisplayOrderForVertical.get(VerticalOption.Total)!
              .filter(
                metric =>
                  !(!achtEnabled && metric === LighthouseMetricOption.ACHT)
              )
              .map(metric => (
                <TableCell
                  width={widthChange}
                  key={metric + 'HEADER'}
                  alignmentVertical='bottom'
                >
                  <FormattedMessage
                    {...getMetricStringIdAndDefaultMessage(metric)}
                  />
                </TableCell>
              ))
        }

        {newCoachingEnabled && manageCoachingNotes && (
          <TableCell width='10%' alignmentVertical='bottom'>
            <div data-cy='last-coached'>
              <FormattedMessage
                id='cs_perfportal_last_coached'
                defaultMessage='Last Coached'
              />
            </div>
          </TableCell>
        )}
        {newCoachingEnabled && manageCoachingNotes && (
          <TableCell width='10%' alignmentVertical='bottom'>
            <div data-cy='coaching-notes'>
              <FormattedMessage
                id='cs_perfportal_coaching_notes'
                defaultMessage='Coaching notes'
              />
            </div>
          </TableCell>
        )}
        {newCoachingEnabled && manageCoachingNotes && <TableCell width='10%' />}
      </TableRow>
      <TableRow highlightOnHover={false}>
        <TableCell>
          <FormattedMessage
            id='cs_perfportal_direct_reports'
            defaultMessage='Direct reports'
          />
          <br />
          <Tooltip
            position='start'
            title={intl.formatMessage({
              id: 'cs_perfportal_network_benchmark_definition',
              defaultMessage:
                'is the average score of teams with a similar skill mix',
            })}
          >
            <i>
              <Text type='b200' color='secondary'>
                <FormattedMessage
                  id='cs_perfportal_network_benchmark'
                  defaultMessage='Network Benchmark'
                />
              </Text>
            </i>
          </Tooltip>
        </TableCell>

        {/* TopLevel Direct Reports*/}
        {displayLHInOrder(
          vertical,
          DisplayOrderForVertical,
          lighthouseMetrics,
          teamMetricTypes
        )}

        {newCoachingEnabled && manageCoachingNotes && (
          <>
            <TableCell>--</TableCell>
            <TableCell>--</TableCell>
            <TableCell>
              <div style={{ display: 'table', visibility: 'hidden' }}>
                <FormattedMessage
                  id='cs_perfportal_coaching_open'
                  defaultMessage='Open'
                />
              </div>
            </TableCell>
          </>
        )}
      </TableRow>
    </Table>
  );
};

export default Summary;
